import { AlertDialog } from "@base-ui/react/alert-dialog";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import * as React from "react";
import { useState, type ComponentProps, type ReactNode } from "react";
import { useI18n } from "../i18n/index.js";
import { Button } from "./button.jsx";
import { cx } from "./cx.js";
import { XIcon } from "./icons.jsx";

/** Base UI Dialog content: portal + backdrop + centered popup with a standard header. */
function Content({
  title,
  description,
  footer,
  children,
  className,
  ...props
}: Omit<ComponentProps<typeof BaseDialog.Popup>, "title" | "className"> & { className?: string | undefined; title: ReactNode; description?: ReactNode; footer?: ReactNode }) {
  const { t } = useI18n();
  return (
    <BaseDialog.Portal>
      <BaseDialog.Backdrop className="dt-dialog__backdrop" />
      <BaseDialog.Viewport className="dt-dialog__viewport">
        <BaseDialog.Popup className={cx("dt-dialog", className)} {...props}>
          <header className="dt-dialog__header">
            <div>
              <BaseDialog.Title className="dt-dialog__title">{title}</BaseDialog.Title>
              {description && <BaseDialog.Description className="dt-dialog__desc">{description}</BaseDialog.Description>}
            </div>
            <BaseDialog.Close className="dt-btn dt-btn--ghost dt-btn--sm dt-btn--icon" aria-label={t("common.close")}>
              <XIcon />
            </BaseDialog.Close>
          </header>
          <div className="dt-dialog__body">{children}</div>
          {footer && <footer className="dt-dialog__footer">{footer}</footer>}
        </BaseDialog.Popup>
      </BaseDialog.Viewport>
    </BaseDialog.Portal>
  );
}

export const Dialog = {
  Root: BaseDialog.Root,
  Trigger: BaseDialog.Trigger,
  Close: BaseDialog.Close,
  Content,
};

export interface ConfirmDialogProps {
  /** The element that opens the dialog, e.g. a Button. */
  trigger: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Defaults to the translated “Confirm”. */
  confirmLabel?: string;
  /** Defaults to the translated “Cancel”. */
  cancelLabel?: string;
  destructive?: boolean;
  /** Runs on confirm; the dialog stays open (with a spinner) until it settles. */
  onConfirm: () => Promise<unknown> | unknown;
}

/** Base UI AlertDialog for irreversible actions. */
export function ConfirmDialog({ trigger, title, description, confirmLabel, cancelLabel, destructive, onConfirm }: ConfirmDialogProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <AlertDialog.Root open={open} onOpenChange={(next) => !busy && setOpen(next)}>
      <AlertDialog.Trigger render={trigger as React.ReactElement} />
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className="dt-dialog__backdrop" />
        <AlertDialog.Viewport className="dt-dialog__viewport">
          <AlertDialog.Popup className="dt-dialog dt-dialog--sm">
            <header className="dt-dialog__header">
              <div>
                <AlertDialog.Title className="dt-dialog__title">{title}</AlertDialog.Title>
                {description && <AlertDialog.Description className="dt-dialog__desc">{description}</AlertDialog.Description>}
              </div>
            </header>
            {error && (
              <div className="dt-dialog__body">
                <p className="dt-field__error" role="alert">
                  {error}
                </p>
              </div>
            )}
            <footer className="dt-dialog__footer">
              <AlertDialog.Close render={<Button variant="ghost" />} disabled={busy}>
                {cancelLabel ?? t("common.cancel")}
              </AlertDialog.Close>
              <Button
                variant={destructive ? "danger" : "primary"}
                loading={busy}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await onConfirm();
                    setOpen(false);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : String(err));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {confirmLabel ?? t("common.confirm")}
              </Button>
            </footer>
          </AlertDialog.Popup>
        </AlertDialog.Viewport>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
